import { AbstractControl } from '@angular/forms';

/**
 * Utilitário de documento (CPF/CNPJ) com suporte ao CNPJ alfanumérico.
 *
 * A partir de 2026 o CNPJ mantém 14 posições e o layout XX.XXX.XXX/XXXX-XX,
 * mas as 12 primeiras posições podem conter letras (A-Z) além de dígitos; os
 * 2 últimos caracteres (DV) continuam numéricos. No cálculo do DV (módulo 11)
 * o valor de cada caractere é o código ASCII menos 48 ('0'=0..'9'=9,
 * 'A'=17..'Z'=42).
 *
 * O algoritmo aqui é o espelho de com.jkawflex.utils.Infokaw (jkaw-domain),
 * garantindo que front e back validem exatamente igual.
 */

/** Remove separadores de máscara, preserva letras/dígitos e força maiúsculas. */
export function limpaDocumento(valor: any): string {
  return (valor == null ? '' : valor.toString()).replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

/** Mantém apenas dígitos (uso para CPF, que é sempre numérico). */
export function apenasDigitos(valor: any): string {
  return (valor == null ? '' : valor.toString()).replace(/[^0-9]/g, '');
}

/**
 * Valida um CNPJ numérico ou alfanumérico pelos dígitos verificadores.
 * Aceita o valor com ou sem máscara.
 */
export function validaCnpj(valor: any): boolean {
  const cnpj = limpaDocumento(valor);
  if (cnpj.length !== 14) {
    return false;
  }
  let d1 = 0;
  let d4 = 0;
  let xx = 0;
  for (let i = 0; i < cnpj.length - 2; i++) {
    const v = cnpj.charCodeAt(i) - 48;
    let fator = xx < 4 ? 5 - xx : 13 - xx;
    d1 += v * fator;
    fator = xx < 5 ? 6 - xx : 14 - xx;
    d4 += v * fator;
    xx++;
  }
  let resto = d1 % 11;
  const digito1 = resto < 2 ? 0 : 11 - resto;
  d4 = d4 + 2 * digito1;
  resto = d4 % 11;
  const digito2 = resto < 2 ? 0 : 11 - resto;
  return `${digito1}${digito2}` === cnpj.substring(cnpj.length - 2);
}

/**
 * Valida um CPF pelos dígitos verificadores. Aceita o valor com ou sem máscara.
 */
export function validaCpf(valor: any): boolean {
  const cpf = apenasDigitos(valor);
  if (cpf.length !== 11) {
    return false;
  }
  let d1 = 0;
  let d4 = 0;
  let xx = 1;
  for (let i = 0; i < cpf.length - 2; i++) {
    const n = cpf.charCodeAt(i) - 48;
    d1 += (11 - xx) * n;
    d4 += (12 - xx) * n;
    xx++;
  }
  let resto = d1 % 11;
  const digito1 = resto < 2 ? 0 : 11 - resto;
  d4 = d4 + 2 * digito1;
  resto = d4 % 11;
  const digito2 = resto < 2 ? 0 : 11 - resto;
  return `${digito1}${digito2}` === cpf.substring(cpf.length - 2);
}

/** Verdadeiro quando o valor é um CPF OU um CNPJ válido. */
export function validaCpfCnpj(valor: any): boolean {
  return validaCpf(valor) || validaCnpj(valor);
}

/**
 * Formata para exibição preservando letras (CNPJ alfanumérico). Retorna o valor
 * original quando não tiver o tamanho de CPF (11) ou CNPJ (14).
 */
export function formataDocumento(valor: any): string {
  const doc = limpaDocumento(valor);
  if (doc.length === 11) {
    return doc.replace(/^([0-9]{3})([0-9]{3})([0-9]{3})([0-9]{2})$/, '$1.$2.$3-$4');
  }
  if (doc.length === 14) {
    return doc.replace(/^([0-9A-Z]{2})([0-9A-Z]{3})([0-9A-Z]{3})([0-9A-Z]{4})([0-9]{2})$/, '$1.$2.$3/$4-$5');
  }
  return valor;
}

/**
 * Validador Angular reutilizável. Considera válido o valor vazio/placeholder
 * (a obrigatoriedade fica a cargo de Validators.required onde necessário).
 */
export function validadorCpfCnpj(controle: AbstractControl) {
  const valor = controle.value;
  if (!valor || valor === '000.000.000-00' || valor === '00.000.000/0000-00') {
    return null;
  }
  return validaCpfCnpj(valor) ? null : { cpfCnpjInvalido: true };
}

// ---------------------------------------------------------------------------
// Máscaras imask centralizadas (usar via [imask]="MASK_*").
// '0' = dígito; a definição 'S' = letra ou dígito (12 primeiras posições do
// CNPJ). O DV permanece '0' (numérico). 'prepare' força maiúsculas.
// ---------------------------------------------------------------------------

export const MASK_CPF = {
  mask: '000.000.000-00',
  lazy: true,
  placeholderChar: '_',
};

export const MASK_CNPJ = {
  mask: 'SS.SSS.SSS/SSSS-00',
  definitions: { S: /[0-9A-Za-z]/ },
  prepare: (str: string) => (str || '').toUpperCase(),
  lazy: true,
  placeholderChar: '_',
};
